"use client";

import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, Users, AlertTriangle, Mail, Phone, FileText } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useLanguage } from '@/contexts/language-context';

const AnimatedBackground = dynamic(() => import('@/components/animated-background'), { ssr: false });

const PrivacyPage = () => {
  const { t, language } = useLanguage();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 100
      }
    }
  };

  const dataTypes = [
    {
      type: language === 'nl' ? 'Contactgegevens' : 'Contact Information',
      details: language === 'nl' ? 'Naam, e-mailadres, telefoonnummer' : 'Name, email address, phone number',
      icon: Users
    },
    {
      type: language === 'nl' ? 'Technische gegevens' : 'Technical Data',
      details: language === 'nl' ? 'IP-adres, browsertype, apparaatinformatie' : 'IP address, browser type, device information',
      icon: Database
    },
    {
      type: language === 'nl' ? 'Gebruiksgegevens' : 'Usage Data',
      details: language === 'nl' ? 'Hoe u onze website gebruikt en interacties' : 'How you use our website and interactions',
      icon: Eye
    }
  ];

  const rights = [
    {
      title: language === 'nl' ? 'Recht op toegang' : 'Right of access',
      description: language === 'nl' ? 'U kunt een overzicht opvragen van uw persoonsgegevens' : 'You can request an overview of your personal data'
    },
    {
      title: language === 'nl' ? 'Recht op rectificatie' : 'Right of rectification',
      description: language === 'nl' ? 'U kunt onjuiste gegevens laten corrigeren' : 'You can have incorrect data corrected'
    },
    {
      title: language === 'nl' ? 'Recht op vergetelheid' : 'Right to be forgotten',
      description: language === 'nl' ? 'U kunt verzoeken om verwijdering van uw gegevens' : 'You can request deletion of your data'
    },
    {
      title: language === 'nl' ? 'Recht op gegevensoverdraagbaarheid' : 'Right to data portability',
      description: language === 'nl' ? 'U kunt uw gegevens in een gestructureerd formaat opvragen' : 'You can request your data in a structured format'
    }
  ];

  return (
    <div className="relative min-h-screen">
      <AnimatedBackground />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 mb-6">
                <Shield className="w-10 h-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-deep-purple dark:text-cream mb-6">
              {language === 'nl' ? 'Privacybeleid' : 'Privacy Policy'}
            </h1>
            <p className="text-xl text-deep-purple/80 dark:text-cream/80 mb-8 leading-relaxed max-w-3xl mx-auto">
              {language === 'nl' 
                ? 'Agnus Cloud respecteert uw privacy en beschermt uw persoonsgegevens. In dit privacybeleid leggen wij uit hoe wij omgaan met uw gegevens en welke rechten u heeft.'
                : 'Agnus Cloud respects your privacy and protects your personal data. In this privacy policy, we explain how we handle your data and what rights you have.'
              }
            </p>
          </motion.div>
        </div>
      </section>

      {/* GDPR Compliance Notice */}
      <section className="relative py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-200 dark:border-green-700">
            <CardContent className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <Lock className="w-8 h-8 text-green-600" />
                <h2 className="text-2xl font-bold text-green-600">
                  {language === 'nl' ? 'AVG Compliance' : 'GDPR Compliance'}
                </h2>
              </div>
              <p className="text-deep-purple/80 dark:text-cream/80 leading-relaxed">
                {language === 'nl'
                  ? 'Dit privacybeleid is opgesteld conform de Algemene Verordening Gegevensbescherming (AVG) en andere toepasselijke privacywetgeving. Wij nemen uw privacy serieus en implementeren passende technische en organisatorische maatregelen om uw gegevens te beschermen.'
                  : 'This privacy policy is drafted in accordance with the General Data Protection Regulation (GDPR) and other applicable privacy legislation. We take your privacy seriously and implement appropriate technical and organizational measures to protect your data.'
                }
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </section>

      {/* Privacy Policy Content */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-12"
          >
            {/* 1. Data Collection */}
            <motion.div variants={itemVariants}>
              <Card className="bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="p-3 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500"
                    >
                      <Database className="w-6 h-6 text-white" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-deep-purple dark:text-cream">
                      1. {language === 'nl' ? 'Welke gegevens verzamelen wij?' : 'What data do we collect?'}
                    </h2>
                  </div>
                  <div className="space-y-6">
                    <p className="text-deep-purple/80 dark:text-cream/80">
                      {language === 'nl' 
                        ? 'Wij verzamelen alleen gegevens die noodzakelijk zijn voor het leveren van onze diensten en het verbeteren van uw ervaring:'
                        : 'We only collect data that is necessary for delivering our services and improving your experience:'
                      }
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {dataTypes.map((data, index) => (
                        <motion.div 
                          key={index}
                          whileHover={{ scale: 1.02 }}
                          className="p-4 rounded-lg bg-gradient-to-br from-cloud-blue/5 to-cloud-lavender/5 border border-cloud-blue/20"
                        >
                          <data.icon className="w-6 h-6 text-cloud-blue mb-3" />
                          <h3 className="font-semibold text-deep-purple dark:text-cream mb-2">
                            {data.type}
                          </h3>
                          <p className="text-sm text-deep-purple/70 dark:text-cream/70">
                            {data.details}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* 2. Purpose of Processing */}
            <motion.div variants={itemVariants}>
              <Card className="bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: -5 }}
                      className="p-3 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500"
                    >
                      <FileText className="w-6 h-6 text-white" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-deep-purple dark:text-cream">
                      2. {language === 'nl' ? 'Waarom gebruiken wij uw gegevens?' : 'Why do we use your data?'}
                    </h2>
                  </div>
                  <div className="space-y-4">
                    <p className="text-deep-purple/80 dark:text-cream/80">
                      {language === 'nl' 
                        ? 'Wij gebruiken uw persoonsgegevens uitsluitend voor de volgende doeleinden:'
                        : 'We use your personal data exclusively for the following purposes:'
                      }
                    </p>
                    <ul className="space-y-3 text-deep-purple/70 dark:text-cream/70">
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span>
                          {language === 'nl'
                            ? 'Het leveren van onze IT-diensten en technische ondersteuning'
                            : 'Delivering our IT services and technical support'
                          }
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span>
                          {language === 'nl'
                            ? 'Communicatie over uw projecten en dienstverlening'
                            : 'Communication about your projects and service delivery'
                          }
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span>
                          {language === 'nl'
                            ? 'Verbetering van onze website en diensten'
                            : 'Improving our website and services'
                          }
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span>
                          {language === 'nl'
                            ? 'Voldoen aan wettelijke verplichtingen'
                            : 'Complying with legal obligations'
                          }
                        </span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* 3. Data Security */}
            <motion.div variants={itemVariants}>
              <Card className="bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="p-3 rounded-full bg-gradient-to-br from-green-500 to-emerald-500"
                    >
                      <Lock className="w-6 h-6 text-white" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-deep-purple dark:text-cream">
                      3. {language === 'nl' ? 'Hoe beschermen wij uw gegevens?' : 'How do we protect your data?'}
                    </h2>
                  </div>
                  <div className="space-y-4">
                    <p className="text-deep-purple/80 dark:text-cream/80">
                      {language === 'nl' 
                        ? 'Wij implementeren passende technische en organisatorische beveiligingsmaatregelen om uw persoonsgegevens te beschermen tegen ongeautoriseerde toegang, verlies, misbruik of openbaarmaking.'
                        : 'We implement appropriate technical and organizational security measures to protect your personal data against unauthorized access, loss, misuse or disclosure.'
                      }
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg bg-gradient-to-br from-green-500/5 to-emerald-500/5 border border-green-200 dark:border-green-700">
                        <h3 className="font-semibold text-deep-purple dark:text-cream mb-2">
                          {language === 'nl' ? 'Technische beveiliging' : 'Technical security'}
                        </h3>
                        <p className="text-sm text-deep-purple/70 dark:text-cream/70">
                          {language === 'nl'
                            ? 'SSL-encryptie, firewalls, regelmatige security updates'
                            : 'SSL encryption, firewalls, regular security updates'
                          }
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-gradient-to-br from-green-500/5 to-emerald-500/5 border border-green-200 dark:border-green-700">
                        <h3 className="font-semibold text-deep-purple dark:text-cream mb-2">
                          {language === 'nl' ? 'Organisatorische beveiliging' : 'Organizational security'}
                        </h3>
                        <p className="text-sm text-deep-purple/70 dark:text-cream/70">
                          {language === 'nl'
                            ? 'Toegangscontrole, training medewerkers, privacy by design'
                            : 'Access control, employee training, privacy by design'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* 4. Your Rights */}
            <motion.div variants={itemVariants}>
              <Card className="bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: -5 }}
                      className="p-3 rounded-full bg-gradient-to-br from-orange-500 to-red-500"
                    >
                      <Users className="w-6 h-6 text-white" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-deep-purple dark:text-cream">
                      4. {language === 'nl' ? 'Uw rechten' : 'Your rights'}
                    </h2>
                  </div>
                  <div className="space-y-4">
                    <p className="text-deep-purple/80 dark:text-cream/80">
                      {language === 'nl' 
                        ? 'U heeft verschillende rechten met betrekking tot uw persoonsgegevens:'
                        : 'You have various rights regarding your personal data:'
                      }
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {rights.map((right, index) => (
                        <motion.div 
                          key={index}
                          whileHover={{ scale: 1.02 }}
                          className="p-4 rounded-lg bg-gradient-to-br from-orange-500/5 to-red-500/5 border border-orange-200 dark:border-orange-700"
                        >
                          <h3 className="font-semibold text-deep-purple dark:text-cream mb-2">
                            {right.title}
                          </h3>
                          <p className="text-sm text-deep-purple/70 dark:text-cream/70">
                            {right.description}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* 5. Data Retention */}
            <motion.div variants={itemVariants}>
              <Card className="bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="p-3 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500"
                    >
                      <AlertTriangle className="w-6 h-6 text-white" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-deep-purple dark:text-cream">
                      5. {language === 'nl' ? 'Bewaarperiode' : 'Data retention'}
                    </h2>
                  </div>
                  <div className="space-y-4">
                    <p className="text-deep-purple/70 dark:text-cream/70">
                      {language === 'nl'
                        ? 'Wij bewaren uw persoonsgegevens niet langer dan noodzakelijk voor de doeleinden waarvoor zij zijn verzameld. Na afloop van de bewaarperiode worden uw gegevens veilig vernietigd, tenzij er een wettelijke verplichting bestaat om deze langer te bewaren.'
                        : 'We do not retain your personal data longer than necessary for the purposes for which it was collected. After the retention period expires, your data will be securely destroyed, unless there is a legal obligation to retain it longer.'
                      }
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-cloud-blue/5 to-cloud-lavender/5">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <h2 className="text-3xl font-bold text-deep-purple dark:text-cream mb-8">
            {language === 'nl' ? 'Vragen over uw privacy?' : 'Questions about your privacy?'}
          </h2>
          <p className="text-lg text-deep-purple/70 dark:text-cream/70 mb-8">
            {language === 'nl'
              ? 'Neem contact met ons op voor vragen over dit privacybeleid of het uitoefenen van uw rechten.'
              : 'Contact us for questions about this privacy policy or exercising your rights.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white px-8 py-6 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Link href="mailto:privacy@agnuscloud.com" className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  privacy@agnuscloud.com
                </Link>
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                asChild
                size="lg"
                className="secondary-button px-8 py-6 text-lg font-semibold rounded-full"
              >
                <Link href="tel:+31854883543" className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  +31 85 - 488 354 3
                </Link>
              </Button>
            </motion.div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default PrivacyPage;
